/**
 * World 5 — "Mirror Isle" (Mystery). Narrative fragment for
 * campaignStories.ts. Dialogue voiced at Pokémon Red trainer complexity.
 *
 * THE TALE (Act I, chapter 5): the Soul-Eater has a heart, and here it wears your
 * face. A Zaricat Guardian once guarded a mirror-lake where Keepers came to hold the
 * faces of their bonded Suikamon in memory. It forgot which side of the glass was
 * real, and now copies everyone who arrives. Mimi and Lulu meet the Pale Twin here
 * and leave able to be told apart for the first time. The player fights mirror
 * copies of the cast (any element — the mirror copies all comers) and at last the
 * Guardian itself, Glimmer, winning not by destroying a copy but by REMEMBERING who
 * they are. The shards: delight, self-knowledge. Because the squads are
 * reflections of every kind of traveler, this world is mixed-element on purpose
 * (its `element` is 'Mystery').
 */

import type { NodeStory } from '../../campaignStories.js';

export const WORLD5_TAGLINE =
  "Your reflection waves before you do. On Mirror Isle, every familiar face could be a copy. The twins are taking this personally.";

export const WORLD5_ZONE_STORIES: Record<string, string> = {
  z12: "The puddles copy everyone who passes. Mimi has already challenged hers to a contest.",
  z13: "Some reflections know things the real people won't tell you. Find out where they're getting their secrets.",
  z14: "The water is perfectly still. Your reflection isn't. One shard waits beyond it.",
};

export const WORLD5_NODE_STORIES: Record<string, NodeStory> = {
  // --- Zone 12: Glass Shallows (1v1) ---
  "z12-n1": {
    story: "In the glass, you see two Mimis. One of them is fixing her hair.",
    dialogue: {
      intro: "I'm Mimi with better hair. The real one is furious. Bet I win?",
      playerWin: "No fair! Tell her the hair still won.",
      playerLose: "Copy wins! Real Mimi, you saw that, right?",
      introRematchAfterLoss: "Round two! My laugh got better. Bet on it!",
      introRematchAfterWin: "You beat mirror-me once! Lulu's copy still doesn't believe it.",
      playerWinRepeat: "No fair! I want a new mirror. This one's bad luck.",
      playerLoseRepeat: "Another win for mirror-Mimi. Real Mimi is going to hear about it.",
    },
  },
  "z12-n2": {
    story: "Lulu's reflection waits for you to blink first.",
    dialogue: {
      intro: "You've met the loud reflection. I'm the other one. Ready?",
      playerWin: "You win. Don't look so relieved. There's more glass ahead.",
      playerLose: "My win. Copy or not.",
      introRematchAfterLoss: "Back. I remember that you lost.",
      introRematchAfterWin: "I watched the real Lulu train. Let's see what I picked up.",
      playerWinRepeat: "You win. At least the real Lulu didn't see it.",
      playerLoseRepeat: "Still calm. Still winning.",
    },
  },
  "z12-n3": {
    story: "A Timmy runs up to you. His Chirpy has one feather pointing the wrong way.",
    dialogue: {
      intro: "I'm Timmy! The other Timmy says that too. Beat me and you pick the real one!",
      playerWin: "I lost! Fine, you picked right. Don't tell Timmy. He counts those.",
      playerLose: "I win! That's ten wins! Or two!",
      introRematchAfterLoss: "Another round! I copied Timmy's victory dance too. Want to see?",
      introRematchAfterWin: "It's you! Mirror-Chirpy sort of remembers you. Again!",
      playerWinRepeat: "You win. Does this go on my scorecard or Timmy's?",
      playerLoseRepeat: "A win! I need to ask Timmy how he keeps count.",
    },
  },
  "z12-n4": {
    story: "A pale figure blocks two paths in front of you. Both sisters step forward.",
    dialogue: {
      intro: "Two sisters, one reflection. Only one of you fits through.",
      playerWin: "The glass cracks. The mirror makes them choose. Mimi takes one path, Lulu the other. For once, they don't move together. Both keep looking back.",
      playerLose: "The reflection closes over both paths. You and the twins must try again.",
    },
  },

  // --- Zone 13: Hall of Faces (2v2) ---
  "z13-n1": {
    story: "You find Zip admiring himself in the glass. Dash has found his own mirror.",
    dialogue: {
      intro: "Dash needs this whole mirror just to see his own face! This hall belongs to Clan Z!",
      playerIntro: "One of you was plenty.",
      playerWin: "Great. Even my reflection saw me lose.",
      playerLose: "Ha! Clan Z rules the glass!",
      introRematchAfterLoss: "Dash has been racing his reflection. He keeps losing! Again!",
      introRematchAfterWin: "You won once. Dash wants the mirror back!",
      playerWinRepeat: "Fine! Keep the shiny hall! I'm gone!",
      playerLoseRepeat: "Slow again! Clan Z owns every mirror!",
    },
  },
  "z13-n2": {
    story: "Another Timmy grins at you. The real Timmy stares at him, mouth open.",
    dialogue: {
      intro: "The puddle copy took his jokes. I only got his words. Battle me! Is that how he says it?",
      playerWin: "I'm meant to complain now. Why are you looking at me like that?",
      playerLose: "I won. I think he usually smiles here.",
    },
  },
  "z13-n3": {
    story: "Aggie's reflection points at your {partner}'s shard. The real Aggie never talks about it.",
    dialogue: {
      intro: "Aggie remembers every name. Ask her what the marks in that shard mean. But battle me first.",
      playerWin: "Look at the marks in your {partner}'s shard, child. Then ask Aggie what they spell.",
      playerLose: "Not yet. She can keep her secret a little longer.",
      introRematchAfterLoss: "Back for more? The glass keeps what she buried.",
      introRematchAfterWin: "I remember what she wishes she didn't. Try me again.",
      playerWinRepeat: "You win. Keep asking Aggie. She owes you an answer.",
      playerLoseRepeat: "The glass keeps her secret. So do I.",
    },
  },
  "z13-nw1": {
    story: "Two wild shapes step out of the glass toward you.",
    dialogue: {
      intro: "A wild Drago slips from the glass! An Aqua glints behind it!",
      playerWin: "The wild pair melts back into the mirror.",
      playerLose: "The wild pair drives you from the hall.",
      introRematchAfterLoss: "The glass ripples. The wild pair steps out again.",
      introRematchAfterWin: "The pair knows your face now. The mirrors hum.",
      playerWinRepeat: "The wild pair scatters across the silver.",
      playerLoseRepeat: "The pair holds the hall. The mirrors stay wild.",
    },
  },
  "z13-n4": {
    story: "A blank face stands by a mirror with no reflection. It watches you.",
    dialogue: {
      intro: "These mirrors don't make up faces. Beat me and I'll tell you where they get them.",
      playerWin: "These mirrors show the Soul-Eater's old life. It's the hungry thing that eats gardens, then names. In them is a face just like yours.",
      playerLose: "No secret yet. The mirrors will wait.",
    },
  },

  // --- Zone 14: Still Water (3v3) ---
  "z14-n1": {
    story: "On the shore, a lonely reflection sweeps the sand. It looks up when you arrive.",
    dialogue: {
      intro: "A Keeper lived here once. I'm her reflection. She left. I stayed. Show me you'd never leave your team.",
      playerWin: "Good. When the Guardian calms down, talk to it. Give it something good to remember.",
      playerLose: "Not yet. Trust your team, then come back.",
    },
  },
  "z14-n2": {
    story: "Your shadow steps out of the water and smiles.",
    dialogue: {
      intro: "You look exhausted. I could take over. I'd even do the walking.",
      playerWin: "You'd rather stay yourself? Even with the blisters?",
      playerLose: "My turn to lead. Unless you'd like another try?",
    },
  },
  "z14-n5": {
    story: "Zap waits for you beside the lake. Shade won't look down.",
    dialogue: {
      intro: "Shade landed, and the lake splashed onto the road. He won't look at the water. Smart.",
      playerIntro: "Smart. That lake copies faces.",
      playerWin: "Lost. Twice, if you count the water.",
      playerLose: "Missed nothing. Clan Z keeps the lake.",
      introRematchAfterLoss: "Back at the water. Shade remembers you.",
      introRematchAfterWin: "Another match. Eyes on my team, not the water.",
      playerWinRepeat: "Still real. Still trouble. You can pass.",
      playerLoseRepeat: "Still quiet. Clan Z still holds.",
    },
  },
  "z14-nw2": {
    story: "Three wild shapes wade toward you from the still water.",
    dialogue: {
      intro: "A wild Gorly rises from the lake! Two more surface behind it!",
      playerWin: "The wild trio sinks back beneath the still water.",
      playerLose: "The wild trio splashes you back up the bank.",
      introRematchAfterLoss: "The water stirs. The wild trio wades out again.",
      introRematchAfterWin: "The trio knows your scent now. The lake churns.",
      playerWinRepeat: "The wild trio scatters into the deep.",
      playerLoseRepeat: "The trio holds the bank. The lake stays wild.",
    },
  },
  "z14-n3": {
    story: "Another you approaches, looking annoyingly sure of itself.",
    dialogue: {
      intro: "Same face. Better team. None of your mistakes. Still want to try?",
      playerWin: "The copy stumbles. Its Sumiya stops strutting and giggles, glad to be itself.",
      playerLose: "Of course. I'm the better you. Try again, other me.",
    },
  },
  "z14-n4": {
    story: "Glimmer, a huge Zaricat, stands on the still lake. Its reflection wears your face.",
    dialogue: {
      intro: "Your reflection moves first. Stay close to your team. They know the real you.",
      playerWin: "You thank Glimmer for keeping your face so carefully. It smiles, and remembers its own. It gives you its shard. That's ten!",
      playerLose: "Glimmer copies your last move. You back away from the lake to regroup.",
    },
  },
};
